import { Button } from '@app/components/core/button/Button'
import { TextInput } from '@app/components/core/input/text/TextInput'
import Column from '@app/components/layout/Column'
import Row from '@app/components/layout/Row'
import { InputField } from '@app/components/core/input/InputField'
import { Text } from '@app/components/core/typography/Text'
import { useTranslations } from '@i18n/useLanguage'
import type { TextSectionType } from '@app/components/blocks/contentSection/ContentSection'
import {
  faArrowDown,
  faArrowUp,
  faChevronDown,
  faChevronUp,
  faTrash,
} from '@fortawesome/pro-regular-svg-icons'
import { IconButton } from '@app/components/core/button/IconButton'
import { type Style } from 'inlines'
import { Card } from '../card/Card'
import { lazy, Suspense } from 'react'
import type { PageContentEditorBlockEditorVariant } from './types/pageContentEditorProps'
import { Title } from '@app/components/core/typography/Title'
import { Tooltip } from '@app/components/core/tooltip/Tooltip'

const LexicalRichHtmlInput = lazy(() =>
  import('@app/components/core/input/lexicalRichInput/LexicalRichHtmlInput').then(
    (module) => ({
      default: module.LexicalRichHtmlInput,
    }),
  ),
)

export type TextEditBlock = Omit<TextSectionType, 'truncationThreshold'>

export type ContentBlockEditorProps = {
  block: TextEditBlock
  onChange: (next: TextEditBlock) => void
  onRemove: () => void
  onMoveUp: () => void
  onMoveDown: () => void
  canMoveUp: boolean
  canMoveDown: boolean
  canRemove?: boolean
  variant?: PageContentEditorBlockEditorVariant
  style?: Style
  titlePlaceholder?: string
  bodyPlaceholder?: string
}

export const ContentBlockEditor = ({
  block,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
  canRemove = true,
  variant = 'default',
  style,
  titlePlaceholder,
  bodyPlaceholder,
}: ContentBlockEditorProps) => {
  const translations = useTranslations()
  const t = translations.app.dashboard.pageContentEditor
  const common = translations.app.common

  const richTextInput = (
    <Suspense
      fallback={
        <InputField label={t.bodyLabel}>
          <div
            style={{
              minHeight: '8rem',
              borderRadius: '0.75rem',
              border: '1px solid var(--palette-blue-300)',
              background: 'var(--palette-white)',
              padding: '0.5rem',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Text.Medium color="var(--palette-blue-400)">
              {common.loading}
            </Text.Medium>
          </div>
        </InputField>
      }
    >
      <LexicalRichHtmlInput
        label={t.bodyLabel}
        labelStyle={
          variant === 'creation'
            ? { color: 'var(--brand-secondary)', fontWeight: 500 }
            : undefined
        }
        required={variant === 'creation'}
        value={block.body}
        setValue={(body) => {
          if (body === block.body) {
            return
          }
          onChange({ ...block, body })
        }}
        placeholder={bodyPlaceholder ?? t.bodyPlaceholder}
      />
    </Suspense>
  )

  if (variant === 'creation') {
    return (
      <Column gap="1rem" alignItems="stretch" style={style}>
        <Title.H4
          style={{
            color: 'var(--brand-secondary)',
            fontWeight: 600,
          }}
        >
          {t.creationNewBlockHeading}
        </Title.H4>
        <TextInput
          label={t.creationTitleLabel}
          labelStyle={
            variant === 'creation'
              ? { color: 'var(--brand-secondary)', fontWeight: 500 }
              : undefined
          }
          required={true}
          text={block.title}
          setText={(title) => onChange({ ...block, title })}
          placeholder={titlePlaceholder ?? t.titlePlaceholder}
        />
        {richTextInput}
        <Row
          gap="0.5rem"
          style={{ justifyContent: 'space-between', alignItems: 'center' }}
        >
          <Row gap="0.5rem">
            <Tooltip content={t.moveUp}>
              <IconButton
                aria-label={t.moveUp}
                variant="secondary"
                size="small"
                icon={faArrowUp}
                disabled={!canMoveUp}
                onClick={onMoveUp}
              />
            </Tooltip>
            <Tooltip content={t.moveDown}>
              <IconButton
                aria-label={t.moveDown}
                variant="secondary"
                size="small"
                icon={faArrowDown}
                disabled={!canMoveDown}
                onClick={onMoveDown}
              />
            </Tooltip>
          </Row>
          <Tooltip content={t.removeBlock}>
            <IconButton
              aria-label={t.removeBlock}
              variant="secondary"
              size="small"
              icon={faTrash}
              disabled={!canRemove}
              onClick={onRemove}
            />
          </Tooltip>
        </Row>
      </Column>
    )
  }

  return (
    <Card style={style}>
      <Column gap="1rem" alignItems="stretch">
        <TextInput
          label={t.titleLabel}
          text={block.title}
          setText={(title) => onChange({ ...block, title })}
          placeholder={titlePlaceholder ?? t.titlePlaceholder}
        />
        {richTextInput}
        <Row
          gap="0.5rem"
          style={{ justifyContent: 'flex-end', flexWrap: 'wrap' }}
        >
          <Button
            type="button"
            variant="secondary"
            size="small"
            icon={faChevronUp}
            label={t.moveUp}
            disabled={!canMoveUp}
            onClick={onMoveUp}
          />
          <Button
            type="button"
            variant="secondary"
            size="small"
            icon={faChevronDown}
            label={t.moveDown}
            disabled={!canMoveDown}
            onClick={onMoveDown}
          />
          <Button
            type="button"
            variant="secondary"
            size="small"
            icon={faTrash}
            label={t.removeBlock}
            disabled={!canRemove}
            onClick={onRemove}
          />
        </Row>
      </Column>
    </Card>
  )
}
